import React, { useState, useEffect } from 'react';
import { CharacterCreationData } from './CharacterCreationWizard';
import { EnhancedClass } from '../../types/class-data';
import { BackgroundAnswer, ConnectionAnswer } from '../../types/character';
import { findClassById } from '../../utils/classDataLoader';
import './CharacterCustomization.css';

interface CharacterCustomizationProps {
  characterData: CharacterCreationData;
  onDataChange: (data: Partial<CharacterCreationData>) => void;
}

const CharacterCustomization: React.FC<CharacterCustomizationProps> = ({
  characterData,
  onDataChange,
}) => {
  const [classData, setClassData] = useState<EnhancedClass | null>(null);
  const [appearance, setAppearance] = useState(characterData.appearance || {});
  const [customAppearance, setCustomAppearance] = useState<Record<string, string>>({});
  const [backgroundAnswers, setBackgroundAnswers] = useState<BackgroundAnswer[]>(
    characterData.backgroundAnswers || []
  );
  const [connectionAnswers, setConnectionAnswers] = useState<ConnectionAnswer[]>(
    characterData.connectionAnswers || []
  );
  const [customBackgroundQuestion, setCustomBackgroundQuestion] = useState('');
  const [customConnectionPrompt, setCustomConnectionPrompt] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadClassData = async () => {
      if (characterData.class) {
        try {
          const data = await findClassById(characterData.class);
          setClassData(data);
        } catch (error) {
          console.error('Failed to load class data for customization:', error);
        }
      }
      setLoading(false);
    };

    loadClassData();
  }, [characterData.class]);

  const handleAppearanceChange = (category: string, value: string) => {
    const newAppearance = { ...appearance, [category]: value };
    setAppearance(newAppearance);
    onDataChange({ appearance: newAppearance });
  };

  const handleCustomAppearanceChange = (category: string, value: string) => {
    const newCustom = { ...customAppearance, [category]: value };
    setCustomAppearance(newCustom);
    // Use custom value if provided, otherwise use selected button value
    const newAppearance = { ...appearance, [category]: value || appearance[category as keyof typeof appearance] };
    setAppearance(newAppearance);
    onDataChange({ appearance: newAppearance });
  };

  const handleAddBackgroundAnswer = (suggestionQuestion: string) => {
    if (backgroundAnswers.length < 5) {
      const newAnswers = [...backgroundAnswers, { question: suggestionQuestion, answer: '' }];
      setBackgroundAnswers(newAnswers);
      onDataChange({ backgroundAnswers: newAnswers });
    }
  };

  const handleBackgroundAnswerChange = (index: number, answer: string) => {
    const newAnswers = [...backgroundAnswers];
    newAnswers[index].answer = answer;
    setBackgroundAnswers(newAnswers);
    onDataChange({ backgroundAnswers: newAnswers });
  };

  const handleRemoveBackgroundAnswer = (index: number) => {
    const newAnswers = backgroundAnswers.filter((_, i) => i !== index);
    setBackgroundAnswers(newAnswers);
    onDataChange({ backgroundAnswers: newAnswers });
  };

  const handleAddConnectionAnswer = (suggestionPrompt: string) => {
    if (connectionAnswers.length < 5) {
      const newAnswers = [...connectionAnswers, { prompt: suggestionPrompt, answer: '' }];
      setConnectionAnswers(newAnswers);
      onDataChange({ connectionAnswers: newAnswers });
    }
  };

  const handleConnectionAnswerChange = (index: number, answer: string) => {
    const newAnswers = [...connectionAnswers];
    newAnswers[index].answer = answer;
    setConnectionAnswers(newAnswers);
    onDataChange({ connectionAnswers: newAnswers });
  };

  const handleRemoveConnectionAnswer = (index: number) => {
    const newAnswers = connectionAnswers.filter((_, i) => i !== index);
    setConnectionAnswers(newAnswers);
    onDataChange({ connectionAnswers: newAnswers });
  };

  const handleAddCustomBackgroundQuestion = () => {
    if (customBackgroundQuestion.trim() && backgroundAnswers.length < 5) {
      const newAnswers = [...backgroundAnswers, { question: customBackgroundQuestion.trim(), answer: '' }];
      setBackgroundAnswers(newAnswers);
      onDataChange({ backgroundAnswers: newAnswers });
      setCustomBackgroundQuestion('');
    }
  };

  const handleAddCustomConnectionPrompt = () => {
    if (customConnectionPrompt.trim() && connectionAnswers.length < 5) {
      const newAnswers = [...connectionAnswers, { prompt: customConnectionPrompt.trim(), answer: '' }];
      setConnectionAnswers(newAnswers);
      onDataChange({ connectionAnswers: newAnswers });
      setCustomConnectionPrompt('');
    }
  };

  const handleCustomBackgroundKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddCustomBackgroundQuestion();
    }
  };

  const handleCustomConnectionKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddCustomConnectionPrompt();
    }
  };

  if (loading) {
    return <div className="character-customization__loading">Loading character customization...</div>;
  }

  if (!classData?.characterCreation) {
    return (
      <div className="character-customization__empty">
        <p>This class doesn't have additional customization options.</p>
        <p>You can proceed to the next step.</p>
      </div>
    );
  }

  const { descriptions, backgroundQuestions, connections } = classData.characterCreation;

  return (
    <div className="character-customization">
      <p className="character-customization__description">
        Add personality and depth to your character. These details are optional but help bring your character to life.
      </p>

      <div className="customization-sections">
        {/* Appearance Customization */}
        {descriptions && (
          <div className="customization-section">
            <h3>Appearance & Personality</h3>
            <p className="section-description">
              Click suggestions or enter your own description for each aspect.
            </p>

            <div className="appearance-categories">
              {Object.entries(descriptions).map(([category, options]) => (
                <div key={category} className="appearance-category">
                  <h4>{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
                  <div className="appearance-options">
                    {options.map((option: string) => (
                      <button
                        key={option}
                        className={`appearance-option ${
                          appearance[category as keyof typeof appearance] === option ? 'appearance-option--selected' : ''
                        }`}
                        onClick={() => handleAppearanceChange(category, option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                  <div className="appearance-custom-input">
                    <input
                      type="text"
                      placeholder="Or type your own..."
                      value={customAppearance[category] || ''}
                      onChange={(e) => handleCustomAppearanceChange(category, e.target.value)}
                      className="custom-appearance-field"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Background Questions */}
        {backgroundQuestions && backgroundQuestions.length > 0 && (
          <div className="customization-section">
            <h3>Character Background</h3>
            <p className="section-description">
              Click a suggestion to add it, then answer in your own words. You can add up to 5 background answers.
            </p>

            <div className="background-suggestions">
              <div className="suggestion-buttons">
                {backgroundQuestions.map((question, index) => (
                  <button
                    key={index}
                    className="suggestion-button"
                    onClick={() => handleAddBackgroundAnswer(question)}
                    disabled={backgroundAnswers.length >= 5}
                    title={backgroundAnswers.length >= 5 ? "Maximum 5 answers reached" : "Click to add this question"}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            {backgroundAnswers.length < 5 && (
              <div className="custom-input-section">
                <div className="custom-input-group">
                  <input
                    type="text"
                    placeholder="Or type your own question..."
                    value={customBackgroundQuestion}
                    onChange={(e) => setCustomBackgroundQuestion(e.target.value)}
                    onKeyPress={handleCustomBackgroundKeyPress}
                    className="custom-question-input"
                  />
                  <button
                    className="add-custom-button"
                    onClick={handleAddCustomBackgroundQuestion}
                    disabled={!customBackgroundQuestion.trim()}
                    title="Add your custom question"
                  >
                    Add Question
                  </button>
                </div>
              </div>
            )}

            <div className="background-answers">
              {backgroundAnswers.map((item, index) => (
                <div key={index} className="answer-item">
                  <div className="answer-header">
                    <div className="answer-question">{item.question}</div>
                    <button
                      className="remove-button"
                      onClick={() => handleRemoveBackgroundAnswer(index)}
                      title="Remove this answer"
                    >
                      ✕
                    </button>
                  </div>
                  <textarea
                    className="answer-textarea"
                    placeholder="Your answer..."
                    value={item.answer}
                    onChange={(e) => handleBackgroundAnswerChange(index, e.target.value)}
                    rows={3}
                  />
                </div>
              ))}
              {backgroundAnswers.length === 0 && (
                <p className="no-answers-message">Click a suggestion above to add a background answer.</p>
              )}
            </div>
          </div>
        )}

        {/* Connection Prompts */}
        {connections && connections.length > 0 && (
          <div className="customization-section">
            <h3>Party Connections</h3>
            <p className="section-description">
              Click a suggestion to add it, then answer to establish party bonds. You can add up to 5 connections.
            </p>

            <div className="connection-suggestions">
              <div className="suggestion-buttons">
                {connections.map((prompt, index) => (
                  <button
                    key={index}
                    className="suggestion-button"
                    onClick={() => handleAddConnectionAnswer(prompt)}
                    disabled={connectionAnswers.length >= 5}
                    title={connectionAnswers.length >= 5 ? "Maximum 5 connections reached" : "Click to add this prompt"}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>

            {connectionAnswers.length < 5 && (
              <div className="custom-input-section">
                <div className="custom-input-group">
                  <input
                    type="text"
                    placeholder="Or type your own prompt..."
                    value={customConnectionPrompt}
                    onChange={(e) => setCustomConnectionPrompt(e.target.value)}
                    onKeyPress={handleCustomConnectionKeyPress}
                    className="custom-question-input"
                  />
                  <button
                    className="add-custom-button"
                    onClick={handleAddCustomConnectionPrompt}
                    disabled={!customConnectionPrompt.trim()}
                    title="Add your custom prompt"
                  >
                    Add Prompt
                  </button>
                </div>
              </div>
            )}

            <div className="connection-answers">
              {connectionAnswers.map((item, index) => (
                <div key={index} className="answer-item">
                  <div className="answer-header">
                    <div className="answer-prompt">{item.prompt}</div>
                    <button
                      className="remove-button"
                      onClick={() => handleRemoveConnectionAnswer(index)}
                      title="Remove this connection"
                    >
                      ✕
                    </button>
                  </div>
                  <textarea
                    className="answer-textarea"
                    placeholder="Your answer..."
                    value={item.answer}
                    onChange={(e) => handleConnectionAnswerChange(index, e.target.value)}
                    rows={2}
                  />
                </div>
              ))}
              {connectionAnswers.length === 0 && (
                <p className="no-answers-message">Click a suggestion above to add a party connection.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CharacterCustomization;